import { Pencil, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Customization, SizeSet } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import { CustomizationDialog } from '../components/customization-dialog';
import { DeleteCustomizationDialog } from '../components/delete-customization-dialog';
import { useCustomizations } from '../hooks/use-customizations';
import { useSizeSets } from '../hooks/use-size-sets';

type Editing = { kind: 'new' } | { kind: 'edit'; row: Customization } | null;

function Rules({ row, sizeSets }: { row: Customization; sizeSets: readonly SizeSet[] }) {
  const { t } = useTranslation('dictionary');
  const parts: string[] = [];
  if (row.inputType === 'select') {
    const sizeSet = row.sizeSetId ? sizeSets.find((s) => s.id === row.sizeSetId) : undefined;
    parts.push(
      row.sizeSetId
        ? t('rules.sizeSet', {
            name: sizeSet?.name ?? t('rules.sizeSetUnknown'),
            count: sizeSet?.values.length ?? 0,
          })
        : t('rules.options', { count: row.options?.length ?? 0 }),
    );
  }
  if (row.inputType === 'text' && row.maxLength !== null) {
    parts.push(t('rules.maxLength', { count: row.maxLength }));
  }
  if (row.inputType === 'file') {
    parts.push(
      row.accept && row.accept.length > 0
        ? t('rules.accept', { types: row.accept.map((a) => a.split('/')[1]).join(', ') })
        : t('rules.acceptAny'),
    );
  }
  if (row.affectsLeadTimeDays > 0)
    parts.push(t('rules.leadTime', { count: row.affectsLeadTimeDays }));
  return <span className="text-muted-foreground text-xs">{parts.join(' · ')}</span>;
}

/** Body of the Customizations tab; the catalog setup layout supplies the page header. */
function Customizations() {
  const { t } = useTranslation('dictionary');
  const format = useFormat();
  const query = useCustomizations();
  const sizeSets = useSizeSets();
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<Customization | null>(null);
  const rows = query.data;

  // The tour points at the toolbar button; the empty state's copy of it carries no anchor.
  const newButton = (tour?: string) => (
    <Can permission="attributes.write">
      <Button onClick={() => setEditing({ kind: 'new' })} data-tour={tour}>
        <Plus aria-hidden />
        {t('new')}
      </Button>
    </Can>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-muted-foreground max-w-prose text-sm">{t('description')}</p>
        {newButton('dictionary.customizations.new-button')}
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : !rows ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={SlidersHorizontal}
          title={t('empty.title')}
          description={t('empty.description')}
          action={newButton()}
          tour="dictionary.customizations.empty"
          tourLink
        />
      ) : (
        <Card className="py-0" data-tour="dictionary.customizations.table">
          <CardContent className="px-0">
            <Table aria-label={t('title')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">{t('columns.customization')}</TableHead>
                  <TableHead>{t('columns.inputType')}</TableHead>
                  <TableHead>{t('columns.required')}</TableHead>
                  <TableHead
                    className="text-right"
                    data-tour="dictionary.customizations.price-delta"
                  >
                    {t('columns.priceDelta')}
                  </TableHead>
                  <TableHead className="pr-6 text-right">{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} aria-label={row.label}>
                    <TableCell className="pl-6 align-top">
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-medium">{row.label}</span>
                        <code className="text-muted-foreground text-xs">{row.key}</code>
                        <Rules row={row} sizeSets={sizeSets.data ?? []} />
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant="secondary" className="font-normal">
                        {t(`inputType.${row.inputType}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground align-top text-sm">
                      {row.isRequired ? t('required.yes') : t('required.no')}
                    </TableCell>
                    <TableCell className="text-right align-top text-sm tabular-nums">
                      {row.priceDeltaCents === 0
                        ? '—'
                        : `+${format.money({ amount: row.priceDeltaCents, currency: BASE_CURRENCY })}`}
                    </TableCell>
                    <TableCell className="pr-6 text-right align-top">
                      <Can permission="attributes.write">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditing({ kind: 'edit', row })}
                          >
                            <Pencil aria-hidden />
                            {t('actions.edit', { label: row.label })}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive"
                            aria-label={t('actions.delete', { label: row.label })}
                            onClick={() => setDeleting(row)}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        </div>
                      </Can>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <CustomizationDialog
        customization={editing?.kind === 'edit' ? editing.row : null}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
      <DeleteCustomizationDialog
        customization={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}

export function CustomizationsPage() {
  return (
    <FeatureErrorBoundary name="dictionary.customizations">
      <Customizations />
    </FeatureErrorBoundary>
  );
}
